import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Banknote, ChevronRight, Clock, RefreshCw, X } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const formatMoney = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: String(currency || 'GBP').toUpperCase()
}).format(Number(amount || 0));

const formatDate = (timestamp, includeTime = false) => timestamp
  ? new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
      timeZone: 'Europe/London'
    }).format(new Date(timestamp * 1000))
  : '—';

const scheduleLabel = schedule => {
  if (!schedule?.interval) return 'Managed in Stripe';
  if (schedule.interval === 'daily') return `Daily · ${schedule.delayDays || 0}-day settlement delay`;
  if (schedule.interval === 'weekly') return `Weekly${schedule.weeklyAnchor ? ` · ${schedule.weeklyAnchor}` : ''}`;
  if (schedule.interval === 'monthly') return `Monthly${schedule.monthlyAnchor ? ` · day ${schedule.monthlyAnchor}` : ''}`;
  if (schedule.interval === 'manual') return 'Manual payouts';
  return schedule.interval;
};

const statusLabel = status => String(status || 'unknown').replaceAll('_', ' ');
const today = new Date();
const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

const isoLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const monthDates = month => {
  const [year, monthNumber] = String(month || '').split('-').map(Number);
  if (!year || !monthNumber) return {};
  const fromDate = `${year}-${String(monthNumber).padStart(2, '0')}-01`;
  const toDate = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
  return { fromDate, toDate };
};

function PayoutTable({ payouts, onSelect }) {
  return (
    <div className="admin-payout-table-shell">
      <table className="admin-payout-table">
        <thead><tr><th>Amount</th><th>Status</th><th>Expected</th><th>Created</th><th>Method</th><th>Reference</th><th aria-label="Open" /></tr></thead>
        <tbody>
          {payouts.map(payout => (
            <tr
              key={payout.id}
              className="admin-payout-table-row"
              onClick={() => onSelect(payout)}
              onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') onSelect(payout); }}
              role="button"
              tabIndex={0}
            >
              <td data-label="Amount"><strong>{formatMoney(payout.amount, payout.currency)}</strong></td>
              <td data-label="Status"><span className={`admin-payout-status is-${payout.status}`}>{statusLabel(payout.status)}</span></td>
              <td data-label="Expected">{formatDate(payout.arrivalDate)}</td>
              <td data-label="Created">{formatDate(payout.created)}</td>
              <td data-label="Method" className="capitalize">{payout.method || 'standard'}</td>
              <td data-label="Reference"><code>{payout.id}</code></td>
              <td data-label="View" className="admin-payout-open"><ChevronRight /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutDrawer({ payout, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.getAdminPayoutTransactions(payout.id)
      .then(result => { if (active) setDetails(result); })
      .catch(err => { if (active) setError(err.response?.data?.detail || 'Payout transactions could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [payout.id]);

  useEffect(() => {
    const closeOnEscape = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const summary = details?.payout || payout;

  return (
    <div className="admin-drawer-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="admin-drawer admin-payout-drawer" aria-modal="true" role="dialog" aria-labelledby="payout-drawer-title">
        <div className="admin-drawer-heading">
          <div><span>Payout details</span><h3 id="payout-drawer-title">{formatMoney(summary.amount, summary.currency)}</h3></div>
          <button type="button" onClick={onClose} aria-label="Close payout details"><X /></button>
        </div>

        <div className="admin-payout-drawer-summary">
          <div><span>Status</span><strong><span className={`admin-payout-status is-${summary.status}`}>{statusLabel(summary.status)}</span></strong></div>
          <div><span>Expected</span><strong>{formatDate(summary.arrivalDate)}</strong></div>
          <div><span>Method</span><strong className="capitalize">{summary.method || 'standard'}</strong></div>
          <div><span>Created</span><strong>{formatDate(summary.created, true)}</strong></div>
        </div>

        <div className="admin-payout-reference"><span>Payout reference</span><code>{summary.id}</code></div>

        <div className="admin-payout-drawer-heading-row">
          <div><h4>Transactions</h4><p>Charges, refunds and fees reconciled into this payout.</p></div>
          {details && (
            <div className="admin-payout-drawer-fees">
              <span>Total fees</span>
              <strong>{formatMoney(details.totalFees, details.feeCurrency || summary.currency)}</strong>
              <small>{details.transactions?.length || 0} transactions shown</small>
            </div>
          )}
        </div>

        {loading ? (
          <div className="admin-payout-drawer-loading"><RefreshCw className="animate-spin" /> Loading transactions…</div>
        ) : error ? (
          <div className="admin-payout-message is-error"><AlertCircle /><div><strong>Transactions unavailable</strong><span>{error}</span></div></div>
        ) : details?.transactionsAvailable === false ? (
          <div className="admin-payout-message"><Banknote /><div><strong>Transaction list unavailable</strong><span>{details.transactionMessage}</span></div></div>
        ) : details?.transactions?.length ? (
          <div className="admin-payout-transaction-list">
            <div className="admin-payout-transaction-header"><span>Transaction</span><span>Gross</span><span>Fee</span><span>Net</span></div>
            {details.transactions.map(transaction => (
              <div className="admin-payout-transaction-row" key={transaction.id}>
                <div>
                  <strong>{transaction.orderNumber ? `Order #${transaction.orderNumber}` : 'Sale'}</strong>
                  <span>{formatDate(transaction.created, true)} · {statusLabel(transaction.reportingCategory || transaction.type)}</span>
                  <code>{transaction.id}</code>
                </div>
                <span data-label="Gross">{formatMoney(transaction.amount, transaction.currency)}</span>
                <span data-label="Fee">{formatMoney(transaction.fee, transaction.currency)}</span>
                <strong data-label="Net">{formatMoney(transaction.net, transaction.currency)}</strong>
              </div>
            ))}
            {details.hasMore && <p className="admin-payout-drawer-note">This payout contains more than 100 Stripe balance transactions. The first 100 are shown.</p>}
          </div>
        ) : (
          <div className="admin-payout-message"><Banknote /><div><strong>No reconciled transactions yet</strong><span>Stripe may still be preparing the transaction breakdown for this payout.</span></div></div>
        )}
      </aside>
    </div>
  );
}

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyCursor, setHistoryCursor] = useState(null);
  const [previousCursors, setPreviousCursors] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [filterMode, setFilterMode] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(thisMonth);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const filterParams = () => {
    const now = new Date();
    const todayValue = isoLocalDate(now);
    if (filterMode === 'today') return { fromDate: todayValue, toDate: todayValue };
    if (filterMode === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const value = isoLocalDate(yesterday);
      return { fromDate: value, toDate: value };
    }
    if (filterMode === 'week') {
      const monday = new Date(now);
      const day = monday.getDay() || 7;
      monday.setDate(monday.getDate() - day + 1);
      return { fromDate: isoLocalDate(monday), toDate: todayValue };
    }
    if (filterMode === 'month') return { fromDate: `${todayValue.slice(0, 7)}-01`, toDate: todayValue };
    if (filterMode === 'specific_month') return monthDates(selectedMonth);
    if (filterMode === 'range') return {
      ...(rangeStart ? { fromDate: rangeStart } : {}),
      ...(rangeEnd ? { toDate: rangeEnd } : {})
    };
    return {};
  };

  const load = async (cursor = historyCursor) => {
    setLoading(true);
    setError('');
    try {
      setData(await api.getAdminPayouts({ pageSize: 15, ...filterParams(), ...(cursor ? { startingAfter: cursor } : {}) }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Stripe payout information could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(historyCursor); }, [historyCursor, filterMode, selectedMonth, rangeStart, rangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetHistory = () => {
    setPreviousCursors([]);
    setHistoryCursor(null);
  };

  const updateFilterMode = event => {
    resetHistory();
    setFilterMode(event.target.value);
  };

  const refresh = () => {
    if (historyCursor) {
      setPreviousCursors([]);
      setHistoryCursor(null);
    } else {
      load(null);
    }
  };

  const nextPage = () => {
    if (!data?.historicalNextCursor) return;
    setPreviousCursors(current => [...current, historyCursor]);
    setHistoryCursor(data.historicalNextCursor);
  };

  const previousPage = () => {
    if (!previousCursors.length) return;
    const previous = previousCursors[previousCursors.length - 1];
    setPreviousCursors(current => current.slice(0, -1));
    setHistoryCursor(previous);
  };

  const primaryCurrency = useMemo(() => {
    const entries = [...(data?.balance?.available || []), ...(data?.balance?.pending || [])];
    return entries.find(item => item.currency === 'GBP')?.currency || entries[0]?.currency || data?.historicalPayouts?.[0]?.currency || 'GBP';
  }, [data]);
  const available = data?.balance?.available?.find(item => item.currency === primaryCurrency)?.amount || 0;
  const pending = data?.balance?.pending?.find(item => item.currency === primaryCurrency)?.amount || 0;
  const pageNumber = previousCursors.length + 1;

  return (
    <div className="admin-view space-y-5">
      <AdminTopbarPortal>
        <div className="admin-topbar-controls">
          <button type="button" onClick={refresh} disabled={loading} className="admin-secondary-button">
            <RefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </AdminTopbarPortal>

      {loading && !data ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="admin-payout-message is-error"><AlertCircle /><div><strong>Payouts unavailable</strong><span>{error}</span></div></div>
      ) : !data?.configured ? (
        <div className="admin-payout-message"><Banknote /><div><strong>Connect Stripe to view payouts</strong><span>Add this restaurant’s Stripe secret key in Settings → Payments.</span></div></div>
      ) : (
        <>
          {!data.livemode && <div className="admin-payout-test-banner">Test-mode Stripe data</div>}

          <div className="admin-payout-metrics">
            <div><span>Available balance</span><strong>{formatMoney(available, primaryCurrency)}</strong><small>Available for Stripe’s next payout</small></div>
            <div><span>Pending balance</span><strong>{formatMoney(pending, primaryCurrency)}</strong><small>Still completing Stripe settlement</small></div>
            <div>
              <span>Next payout</span>
              <strong>{data.nextPayout ? formatMoney(data.nextPayout.amount, data.nextPayout.currency) : 'Not scheduled'}</strong>
              <small>{data.nextPayout ? `Expected ${formatDate(data.nextPayout.arrivalDate)}` : scheduleLabel(data.schedule)}</small>
            </div>
            <div>
              <span>Total fees</span>
              <strong>{formatMoney(data.totalFees?.find(item => item.currency === primaryCurrency)?.amount || 0, primaryCurrency)}</strong>
              <small>All-time Stripe processing fees</small>
            </div>
          </div>

          <div className="admin-payout-schedule">
            <Clock />
            <div><strong>Payout schedule</strong><span>{scheduleLabel(data.schedule)}</span></div>
          </div>

          <section className="admin-payout-section">
            <div className="admin-payout-table-heading">
              <div><h2>Upcoming Payouts</h2><p>Money Stripe has scheduled for this restaurant.</p></div>
              {!!data.upcomingPayouts?.length && <span>{data.upcomingPayouts.length} scheduled</span>}
            </div>
            {data.upcomingPayouts?.length ? (
              <PayoutTable payouts={data.upcomingPayouts} onSelect={setSelectedPayout} />
            ) : (
              <div className="admin-payout-upcoming-empty">
                <Banknote />
                <span>No upcoming payouts scheduled, but you have <strong>{formatMoney(pending, primaryCurrency)}</strong> in pending.</span>
              </div>
            )}
          </section>

          <section className="admin-payout-section">
            <div className="admin-payout-table-heading">
              <div><h2>Historical Payouts</h2><p>Completed, failed and cancelled payouts from Stripe.</p></div>
              <span>Page {pageNumber}</span>
            </div>
            <div className="admin-payout-history-tools">
              <div className="admin-toolbar admin-payout-filters">
                <select value={filterMode} onChange={updateFilterMode} disabled={loading} aria-label="Filter historical payouts by period">
                  <option value="all">All dates</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="specific_month">Specific month</option>
                  <option value="range">Between dates</option>
                </select>
                {filterMode === 'specific_month' && (
                  <input type="month" value={selectedMonth} disabled={loading} onChange={event => { resetHistory(); setSelectedMonth(event.target.value); }} aria-label="Payout month" />
                )}
                {filterMode === 'range' && (
                  <>
                    <input type="date" value={rangeStart} max={rangeEnd || undefined} disabled={loading} onChange={event => { resetHistory(); setRangeStart(event.target.value); }} aria-label="Payout period start" />
                    <span className="admin-payout-filter-separator">to</span>
                    <input type="date" value={rangeEnd} min={rangeStart || undefined} disabled={loading} onChange={event => { resetHistory(); setRangeEnd(event.target.value); }} aria-label="Payout period end" />
                  </>
                )}
              </div>
              <div className="admin-payout-history-totals">
                <div><span>Total paid out</span><strong>{formatMoney(data.historicalTotals?.payouts?.find(item => item.currency === primaryCurrency)?.amount || 0, primaryCurrency)}</strong></div>
                <div><span>Total fees</span><strong>{formatMoney(data.historicalTotals?.fees?.find(item => item.currency === primaryCurrency)?.amount || 0, primaryCurrency)}</strong></div>
              </div>
            </div>
            <div className={`admin-payout-history-results ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
              {data.historicalPayouts?.length ? (
                <PayoutTable payouts={data.historicalPayouts} onSelect={setSelectedPayout} />
              ) : (
                <div className="admin-empty-state"><Banknote />No historical payouts were found.</div>
              )}
              {loading && (
                <div className="admin-payout-history-loading" role="status">
                  <RefreshCw className="animate-spin" />
                  <span>Updating payouts…</span>
                </div>
              )}
            </div>
            <div className="admin-pagination">
              <button type="button" onClick={previousPage} disabled={!previousCursors.length || loading}>Previous</button>
              <span>Page {pageNumber}</span>
              <button type="button" onClick={nextPage} disabled={!data.historicalHasMore || loading}>Next</button>
            </div>
          </section>
        </>
      )}

      {selectedPayout && <PayoutDrawer payout={selectedPayout} onClose={() => setSelectedPayout(null)} />}
    </div>
  );
}
